import { useRef } from 'react'
import { Stack, Text, Group, Box } from '@mantine/core'
import { IconPlus } from '@tabler/icons-react'
import { useArcTranslation } from 'hooks'
import { Button } from 'components/Button'
import type { AddManuallyTabProps } from './interfaces'
import { StudentRow } from './StudentRow'
import { useStyles } from './AddStudentsModalStyles'
import { GRID_TEMPLATE, GRID_TEMPLATE_NO_REMOVE, GRID_GAP } from './columns'

export function AddManuallyTab({
  grades,
  groups,
  students,
  onStudentChange,
  onStudentGroupIdsChange,
  onRemoveStudent,
  onAddStudent,
  onFillDownGrade,
  onFillDownGroup,
  canFillDownGrade,
  canFillDownGroup,
  onCreateGroup,
}: AddManuallyTabProps) {
  const { t } = useArcTranslation()
  const { classes } = useStyles()
  const scrollRef = useRef<HTMLDivElement>(null)

  const handleAdd = () => {
    onAddStudent()
    setTimeout(() => {
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }, 100)
  }

  const showRemove = students.length > 1

  return (
    <Stack spacing={0}>
      <Box
        px={2}
        mb='xs'
        sx={{
          display: 'grid',
          gridTemplateColumns: showRemove ? GRID_TEMPLATE : GRID_TEMPLATE_NO_REMOVE,
          gap: GRID_GAP,
        }}
      >
        <Text weight={600} size='sm'>{t('add_students_modal.first_name')}</Text>
        <Text weight={600} size='sm'>{t('add_students_modal.last_name')}</Text>
        <Text weight={600} size='sm'>{t('add_students_modal.username')}</Text>
        <Group spacing={4}>
          <Text weight={600} size='sm'>{t('add_students_modal.groups_label')}</Text>
          {canFillDownGroup && (
            <Text size='xs' color='violet' weight={500} sx={{ cursor: 'pointer' }} onClick={onFillDownGroup}>
              ({t('student_requests.fill_down')})
            </Text>
          )}
        </Group>
        <Group spacing={4}>
          <Text weight={600} size='sm'>{t('add_students_modal.grade_label')}</Text>
          {canFillDownGrade && (
            <Text size='xs' color='violet' weight={500} sx={{ cursor: 'pointer' }} onClick={onFillDownGrade}>
              ({t('student_requests.fill_down')})
            </Text>
          )}
        </Group>
        {showRemove && <Box />}
      </Box>

      <Box className={classes.studentsContainer} ref={scrollRef}>
        <Stack spacing='xs'>
          {students.map((student, index) => (
            <StudentRow
              key={student.id}
              student={student}
              index={index}
              onChange={onStudentChange}
              onGroupIdsChange={onStudentGroupIdsChange}
              grades={grades}
              groups={groups}
              showRemove={showRemove}
              onRemove={() => onRemoveStudent(index)}
              onCreateGroup={onCreateGroup}
            />
          ))}
        </Stack>

        <Box mt='sm'>
          <Button
            variant='outline'
            color='turquoise'
            leftIcon={<IconPlus size={16} />}
            onClick={handleAdd}
            size='sm'
          >
            {t('add_students_modal.add_more')}
          </Button>
        </Box>
      </Box>
    </Stack>
  )
}
